<template>
  <div class="conversations-container">
    <base-nav title="Чаты" :badges="chatBadges" />
    <base-loader v-if="dataIsLoading" />
    <div
      v-else
      class="conversations-block"
      :class="{ 'conversations-block--chat-open': selectedChatId }"
    >
      <ChatList
        class="conversations-list"
        :conversations="conversations"
        :selected-chat-id="selectedChatId"
        @select="selectChat"
      />

      <ConversationWindow
        class="conversations-chat"
        :conversation-id="selectedChatId"
        :conversation-name="selectedChat?.name"
        :conversation-avatar-url="selectedChat?.avatar_url"
        :show-back-button="Boolean(selectedChatId)"
        @back="clearSelectedChat"
      />
    </div>
  </div>
</template>

<script>
import { useChatStore } from "@/stores/chatStore";

import ChatList from "@/components/Chat/List.vue";
import ConversationWindow from "@/components/ConversationWindow.vue";

export default {
  name: "ChatsPage",

  components: {
    ChatList,
    ConversationWindow,
  },

  computed: {
    chatStore() {
      return useChatStore();
    },

    dataIsLoading() {
      return this.chatStore.dataIsLoading;
    },

    conversations() {
      return this.chatStore.conversations;
    },

    selectedChatId() {
      return this.chatStore.selectedChatId;
    },

    selectedChat() {
      return this.chatStore.selectedChat;
    },

    unreadMessagesCount() {
      return this.chatStore.unreadMessagesCount;
    },

    unreadMessagesCountLabel() {
      return `${this.unreadMessagesCount} ${this.unreadMessagesWord}`;
    },

    unreadMessagesWord() {
      const count = this.unreadMessagesCount;
      const mod10 = count % 10;
      const mod100 = count % 100;

      if (mod10 === 1 && mod100 !== 11) return "пропущенное";

      return "пропущенных";
    },

    chatBadges() {
      const badges = [
        {
          text: this.conversationsCountLabel,
          color: "#666",
          bg: "#f3f4f6",
        },
      ];

      if (this.unreadMessagesCount > 0) {
        badges.push({
          text: this.unreadMessagesCountLabel,
          color: "#fff",
          bg: "#ef4444",
        });
      }

      return badges;
    },

    conversationsCountLabel() {
      return `${this.conversations.length} ${this.conversationsWord}`;
    },

    conversationsWord() {
      const count = this.conversations.length;
      const mod10 = count % 10;
      const mod100 = count % 100;

      if (mod10 === 1 && mod100 !== 11) return "чат";
      if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) {
        return "чата";
      }
      return "чатов";
    },
  },

  methods: {
    async selectChat(id) {
      await this.chatStore.selectChat(id);
      this.syncSelectedChatQuery(id);
    },

    clearSelectedChat() {
      this.chatStore.clearSelectedChat();
      this.syncSelectedChatQuery(null);
    },

    async selectChatFromQuery() {
      const conversationId = this.$route.query.conversation_id;
      if (!conversationId) return;

      await this.chatStore.selectChat(Number(conversationId));
    },

    syncSelectedChatQuery(id) {
      const query = { ...this.$route.query };

      if (id) {
        query.conversation_id = String(id);
      } else {
        delete query.conversation_id;
        delete query.message_id;
      }

      this.$router.replace({ query }).catch(() => {});
    },
  },

  async created() {
    await this.chatStore.fetchConversations();
    await this.selectChatFromQuery();
  },

  watch: {
    "$route.query.conversation_id": {
      async handler(value) {
        if (!value || String(value) === String(this.selectedChatId)) return;

        await this.chatStore.selectChat(Number(value));
      },
    },
  },

  beforeUnmount() {
    this.chatStore.disconnectMessageSubscription();
  },
};
</script>

<style scoped>
.conversations-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.conversations-block {
  display: flex;
  gap: 15px;
  flex: 1;
  min-height: 0;
}

.conversations-chat {
  flex: 1;
  min-width: 0;
  height: 100%;
}

@media (max-width: 768px) {
  .conversations-container {
    height: 100%;
  }

  .conversations-block {
    display: block;
    min-height: 0;
  }

  .conversations-list,
  .conversations-chat {
    width: 100%;
    max-width: none;
    min-width: 0;
    height: 100%;
  }

  .conversations-chat {
    display: none;
  }

  .conversations-block--chat-open .conversations-list {
    display: none;
  }

  .conversations-block--chat-open .conversations-chat {
    display: block;
  }
}
</style>
